/**
 * Zemits Financing Calculator — In-House Financing (IHF) Application v1
 *
 * Two tabs:
 *  - In-House Financing: fixed term, down payment chosen between the plan minimum and 90%, 0% APR.
 *    Standard products: 50% down / 8 months. Lasers (collection `lasers`): 38% down / 16 months.
 *    The last payment absorbs rounding so the payments sum to the price exactly.
 *    CTA is a link to the Zoho "In-House Financing Application" form; the theme's #zoho-modal
 *    delegate opens any forms.zohopublic.* link in its iframe, and the leads-widget
 *    zoho_forms_decorator adds the utm_ params, gclid, fbclid, referrer and
 *    attribution_reference to it.
 *
 * Zoho prefill naming — the one non-obvious part of this file:
 *  - The form prefills a field either by its link name (SingleLine3, Number, ...) or by a
 *    field alias. The fields inherited from ZemitsEquipmentFinancing have aliases
 *    (product, sku, page_url, utm_*, gclid, fbclid, referrer, attribution_reference);
 *    the five fields added for IHF have none, so ihf_plan / ihf_plan_type only arrive
 *    under SingleLine15 / SingleLine23.
 *  - zoho_forms_decorator.js skips any URL that already carries a /^SingleLine\d*$/ param.
 *    So the href carries alias names while the page is open (letting the decorator add
 *    attribution), and switches to link names in finalizeCta(), on the click that opens
 *    the modal. Anything else loses either the plan or the attribution.
 *  - Monthly Financing: Shop Pay Installments, which the shopper picks at checkout. The figure
 *    is an estimate labelled "est. 15% APR, 12 mo" and is hidden above data-spi-max-price
 *    (the $30,000 Shop Pay Installments limit). Its button is a plain /cart/add link that
 *    follows the selected variant.
 *
 * IHF eligibility (Roman, 2026-09-25): IHF is offered only on devices priced above
 * data-ihf-min-price (default $2,000, strictly above). The Liquid block decides whether the
 * product is a device and renders the IHF markup ([data-zf-ihf-only], the IHF pane and CTA)
 * only when some variant qualifies; this file shows or hides it for the selected variant.
 * Without IHF markup the block is a Monthly Financing estimate only.
 *
 * Never calls the cart or checkout APIs; the only cart path is that plain link. Vanilla JS,
 * no dependencies.
 * Config comes from data-attributes on [data-zf-root] (rendered by blocks/financing_calc_ihf.liquid).
 */
(function () {
  'use strict';

  var MAX_PARAM = 255; // zoho_forms_decorator truncates at 255; keep our own values consistent

  // Zoho field alias -> link name, for the params this block owns. The first three work under
  // either name; ihf_plan and ihf_plan_type only arrive under the link name.
  var LINK_NAME = {
    product: 'SingleLine3',
    sku: 'SingleLine4',
    page_url: 'SingleLine1',
    ihf_plan: 'SingleLine15',
    ihf_plan_type: 'SingleLine23',
  };
  // Price and term have no alias either, but their link names do not trip the decorator's
  // SingleLine guard, so they are written under the link name from the start.
  var PRICE_PARAM = 'Number';
  var MONTHS_PARAM = 'Number1';

  function fmt(cents, forceCents) {
    var decimals = forceCents || cents % 100 !== 0 ? 2 : 0;
    return '$' + (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
  // Totals and down payments drop ".00"; recurring payments always show cents.
  function money(cents) { return fmt(cents, false); }
  function money2(cents) { return fmt(cents, true); }

  function amortizedMonthly(principalCents, aprPct, months) {
    var r = aprPct / 100 / 12;
    if (!months) return 0;
    if (r === 0) return Math.round(principalCents / months);
    return Math.round(principalCents * r / (1 - Math.pow(1 + r, -months)));
  }

  function clampPct(pct, min, max) {
    if (isNaN(pct)) return min;
    return Math.min(Math.max(pct, min), max);
  }

  /**
   * Equal monthly payments, with the final payment absorbing the rounding remainder
   * so that down + sum(payments) === price exactly.
   */
  function computePlan(price, dpPct, months) {
    var down = Math.round(price * dpPct / 100);
    var remaining = price - down;
    var monthly = months > 0 ? Math.round(remaining / months) : remaining;
    var last = remaining - monthly * (months - 1);
    // Rounding up can make the final payment negative on very short terms; fall back to rounding down.
    if (last < 0) {
      monthly = Math.floor(remaining / months);
      last = remaining - monthly * (months - 1);
    }
    return {
      dpPct: dpPct,
      months: months,
      down: down,
      remaining: remaining,
      monthly: monthly,
      last: last,
      total: price,
      even: last === monthly,
    };
  }

  // Contract §3 wire format, e.g.
  // "Standard IHF: 50% down $2,300 + 8 x $287.50/mo, 0% APR, total $4,600 + tax & shipping"
  function planText(plan, isLaser) {
    var text = (isLaser ? 'Laser IHF: ' : 'Standard IHF: ') +
      plan.dpPct + '% down ' + money(plan.down) +
      ' + ' + plan.months + ' x ' + money2(plan.monthly) + '/mo' +
      ', 0% APR, total ' + money(plan.total) + ' + tax & shipping';
    if (!plan.even) text += ' (last payment ' + money2(plan.last) + ')';
    return text.slice(0, MAX_PARAM);
  }

  function parseVariants(raw) {
    try {
      var parsed = JSON.parse(raw || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (ignored) {
      return {};
    }
  }

  function init(root) {
    if (root.dataset.zfInit === '1') return;
    root.dataset.zfInit = '1';

    var minPrice = parseInt(root.dataset.minPrice || '0', 10);
    var ihfMinPrice = parseInt(root.dataset.ihfMinPrice || '200000', 10);
    var ihfDevice = root.dataset.ihfDevice === 'true';
    var apr = parseFloat(root.dataset.apr || '15');
    var termMonths = parseInt(root.dataset.term || '12', 10);
    var spiMaxPrice = root.dataset.spiMaxPrice ? parseInt(root.dataset.spiMaxPrice, 10) : Infinity;
    var cartAddUrl = root.dataset.cartAddUrl || '/cart/add';
    var isLaser = root.dataset.planType === 'laser';
    var months = parseInt(root.dataset.months || '8', 10);
    var minDown = parseInt(root.dataset.downPct || '50', 10);
    var maxDown = parseInt(root.dataset.maxDownPct || '90', 10);
    var formUrl = root.dataset.formUrl || '';
    var pageUrl = root.dataset.pageUrl || (location.origin + location.pathname);
    var variants = parseVariants(root.dataset.variants);

    // Current variant state — updated by syncVariant() when the shopper switches variants.
    var price = parseInt(root.dataset.price || '0', 10);
    var sku = root.dataset.sku || '';
    var productName = root.dataset.product || 'Product';
    var variantId = root.dataset.variantId || '';

    var $ = function (sel) { return root.querySelector(sel); };

    var summaryMonthly = $('[data-zf-summary-monthly]');
    var summaryIhf = $('[data-zf-summary-ihf]');
    var toggleBtn = $('[data-zf-toggle]');
    var dpRange = $('[data-zf-dp]');
    var dpVal = $('[data-zf-dp-val]');
    var outDown = $('[data-zf-out-down]');
    var outMonthly = $('[data-zf-out-monthly]');
    var outMonths = $('[data-zf-out-months]');
    var outLast = $('[data-zf-out-last]');
    var outLastRow = $('[data-zf-out-last-row]');
    var outTotal = $('[data-zf-out-total]');
    var outThird = $('[data-zf-out-third]');
    var outThirdTerm = $('[data-zf-out-third-term]');
    var planSummary = $('[data-zf-plan-summary]');
    var cta = $('[data-zf-apply]');
    var cartLink = $('[data-zf-cart]');
    var tablist = $('[data-zf-tablist]');
    var priceOuts = root.querySelectorAll('[data-zf-out-price]');
    var ihfOnly = [].slice.call(root.querySelectorAll('[data-zf-ihf-only]'));
    var spiOnly = [].slice.call(root.querySelectorAll('[data-zf-spi-only]'));
    var tabs = [].slice.call(root.querySelectorAll('[data-zf-tab]'));
    var panes = [].slice.call(root.querySelectorAll('[data-zf-pane]'));

    // The IHF markup exists only when the Liquid block found a qualifying variant.
    var hasIhf = !!dpRange;

    // Hidden below min_price, and when neither In-House Financing nor Shop Pay Installments
    // covers the selected variant.
    function hideIfNothingToShow() {
      var hide = (minPrice > 0 && price < minPrice) || (!ihfAllowed() && !spiAllowed());
      root.style.display = hide ? 'none' : '';
      return hide;
    }
    if (hideIfNothingToShow()) return;

    function ihfAllowed() {
      return hasIhf && ihfDevice && price > ihfMinPrice;
    }
    function spiAllowed() {
      return price <= spiMaxPrice;
    }
    var ihfShown = ihfAllowed();
    var spiShown = spiAllowed();

    function currentPlan() {
      return computePlan(price, clampPct(parseInt(dpRange.value, 10), minDown, maxDown), months);
    }

    function ctaUrl() {
      try {
        return new URL(cta.getAttribute('href') || formUrl, location.href);
      } catch (ignored) {
        return null;
      }
    }

    // Writes the value under whichever of the two names the href currently uses, so that
    // finalizeCta() stays a one-way switch and no param is ever left behind as a stale twin.
    function setOwnParam(url, alias, value) {
      var linkName = LINK_NAME[alias];
      url.searchParams.set(url.searchParams.has(linkName) ? linkName : alias, value);
    }

    /**
     * Update only our own query params, preserving everything already on the href —
     * the leads-widget decorator writes the utm_ params, gclid, fbclid, referrer and
     * attribution_reference onto this same anchor and must not be clobbered on every
     * slider move.
     */
    function updateCta(plan) {
      if (!cta || !formUrl) return;
      var url = ctaUrl();
      if (!url) return;
      setOwnParam(url, 'product', productName);
      setOwnParam(url, 'sku', sku);
      setOwnParam(url, 'page_url', pageUrl);
      setOwnParam(url, 'ihf_plan', planText(plan, isLaser));
      setOwnParam(url, 'ihf_plan_type', isLaser ? 'laser' : 'standard');
      url.searchParams.set(PRICE_PARAM, String(price / 100));
      url.searchParams.set(MONTHS_PARAM, String(plan.months));
      cta.setAttribute('href', url.href);
    }

    /**
     * Switch our params from the Zoho alias to the form's link name, on the click that
     * opens the modal. It has to be this late: the decorator refuses to touch a URL that
     * already carries a SingleLineN param, and by click time it has long since added
     * utm_*, gclid, fbclid, referrer and attribution_reference. Idempotent.
     */
    function finalizeCta() {
      if (!cta || !formUrl) return;
      var url = ctaUrl();
      if (!url) return;
      var changed = false;
      Object.keys(LINK_NAME).forEach(function (alias) {
        if (!url.searchParams.has(alias)) return;
        url.searchParams.set(LINK_NAME[alias], url.searchParams.get(alias));
        url.searchParams.delete(alias);
        changed = true;
      });
      if (changed) cta.setAttribute('href', url.href);
    }

    function recalc() {
      var third = amortizedMonthly(price, apr, termMonths);
      if (outThird) outThird.textContent = money2(third);
      if (outThirdTerm) outThirdTerm.textContent = 'est. ' + apr + '% APR, ' + termMonths + ' mo';
      if (summaryMonthly) summaryMonthly.textContent = money2(third) + '/mo';
      for (var i = 0; i < priceOuts.length; i++) priceOuts[i].textContent = money(price);
      if (cartLink && variantId) {
        cartLink.setAttribute('href', cartAddUrl + '?id=' + encodeURIComponent(variantId) + '&quantity=1');
      }

      if (!hasIhf) return;
      var plan = currentPlan();

      if (dpVal) dpVal.textContent = plan.dpPct + '% (' + money(plan.down) + ')';
      dpRange.setAttribute('aria-valuetext', plan.dpPct + ' percent, ' + money(plan.down) + ' down today');

      if (outDown) outDown.textContent = money(plan.down);
      if (outMonthly) outMonthly.textContent = money2(plan.monthly);
      if (outMonths) outMonths.textContent = plan.months;
      if (outTotal) outTotal.textContent = money(plan.total);
      if (outLastRow) outLastRow.hidden = plan.even;
      if (outLast) outLast.textContent = money2(plan.last);
      if (planSummary) planSummary.textContent = planText(plan, isLaser);

      if (summaryIhf) {
        summaryIhf.textContent = money(plan.down) + ' down + ' + plan.months + ' × ' + money2(plan.monthly) + '/mo';
      }

      updateCta(plan);
    }

    // ---- Variant switching (price / SKU) -------------------------------------------------
    function syncVariant() {
      var id = '';
      try {
        id = new URLSearchParams(location.search).get('variant') || '';
      } catch (ignored) {}
      if (!id || id === variantId || !variants[id]) return;

      var next = variants[id];
      variantId = id;
      price = parseInt(next.price, 10) || price;
      sku = next.sku || '';
      if (next.title) productName = next.title;
      if (hideIfNothingToShow()) return;
      applyAvailability();
      recalc();
    }

    window.addEventListener('popstate', syncVariant);
    // Horizon and most Shopify themes fire one of these on variant selection.
    ['variant:change', 'variant:update', 'on:variant:change'].forEach(function (name) {
      document.addEventListener(name, syncVariant);
    });
    // Fallback: the variant picker writes the id into a hidden [name="id"] input.
    document.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'id') setTimeout(syncVariant, 0);
    });

    // ---- Disclosure ----------------------------------------------------------------------
    if (toggleBtn) {
      toggleBtn.addEventListener('click', function () {
        var open = root.classList.toggle('zf-open');
        toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    if (dpRange) dpRange.addEventListener('input', recalc);

    // Listeners on the anchor itself run before the theme's document-level #zoho-modal
    // delegate, so the modal always receives the finalised href. pointerdown also covers
    // middle-click and "copy link address".
    if (cta) {
      cta.addEventListener('pointerdown', finalizeCta);
      cta.addEventListener('click', finalizeCta);
      cta.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') finalizeCta();
      });
    }

    // ---- Tabs (proper tablist keyboard behaviour) ----------------------------------------
    function selectTab(btn, focus) {
      var key = btn.getAttribute('data-zf-tab');
      tabs.forEach(function (b) {
        var active = b === btn;
        b.classList.toggle('zf-calculator__tab--active', active);
        b.setAttribute('aria-selected', active ? 'true' : 'false');
        b.setAttribute('tabindex', active ? '0' : '-1');
      });
      panes.forEach(function (p) {
        var active = p.getAttribute('data-zf-pane') === key;
        p.classList.toggle('zf-calculator__pane--active', active);
        p.hidden = !active;
      });
      if (focus) btn.focus();
    }

    tabs.forEach(function (btn, index) {
      btn.addEventListener('click', function () { selectTab(btn); });
      btn.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
        else if (e.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
        else if (e.key === 'Home') next = tabs[0];
        else if (e.key === 'End') next = tabs[tabs.length - 1];
        if (next) {
          e.preventDefault();
          selectTab(next, true);
        }
      });
    });

    // ---- Offer visibility for the selected variant ---------------------------------------
    // Each summary line shows only while its offer covers the variant; the tablist only while
    // both do. The IHF tab is the default whenever IHF applies, otherwise Monthly Financing.
    function applyAvailability() {
      var ihfOn = ihfAllowed();
      var spiOn = spiAllowed();
      ihfOnly.forEach(function (el) { el.hidden = !ihfOn; });
      spiOnly.forEach(function (el) { el.hidden = !spiOn; });
      if (tablist) tablist.hidden = !(ihfOn && spiOn);
      if (ihfOn === ihfShown && spiOn === spiShown) return;
      ihfShown = ihfOn;
      spiShown = spiOn;
      var target = root.querySelector(ihfOn ? '[data-zf-tab="ihf"]' : '[data-zf-tab="third"]');
      if (target) selectTab(target);
    }

    applyAvailability();
    recalc();
  }

  function initAll() {
    document.querySelectorAll('[data-zf-root]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  window.addEventListener('shopify:section:load', initAll);
})();
